import './styles.css';

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import {
  $getRoot,
  $isTextNode,
  DOMConversionMap,
  DOMExportOutput,
  DOMExportOutputMap,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
  LineBreakNode,
  ParagraphNode,
  TextNode,
} from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { FormLabel } from '@mui/material';

import { ExampleTheme } from './ExampleTheme';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import TreeViewPlugin from './plugins/TreeViewPlugin';

import ImagesPlugin from './lexical-playground/src/plugins/ImagesPlugin';
import { $importImageElement, ImageNode } from './lexical-playground/src/nodes/ImageNode';

import { CasLexicalInputNumberNode } from './CasLexicalNumber/CasLexicalInputNumberNode';
import CasLexicalInputNumberPlugin from './plugins/CasLexicalInputNumberPlugin';
import { CasNumberBoxDialogHandlerPlugin } from './plugins/CasNumberBoxDialogHandlerPlugin';

import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CasDivNode, $createCasDivNode } from './CasNodes/CasDivNode';

type Props = {
  label?: string;
  placeholder?: string;
  isError?: boolean;
  initialValue?: string;
  onChange?: (value: string) => void;
  mode?: "simple" | "full",
};

function prepareHtmlForEmail(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Add inline style to all <li> elements
  doc.querySelectorAll('li').forEach(li => {
    const existing = li.getAttribute('style') || '';
    li.setAttribute(
      'style',
      `${existing}${existing && !existing.endsWith(';') ? ';' : ''}margin:0;`
    );
  });

  const PRE_WRAP_STYLES = ['white-space: pre-wrap', 'white-space:pre-wrap'];
  const elementsToClean = doc.querySelectorAll('[style*="white-space: pre-wrap"]');

  elementsToClean.forEach((element) => {
    const hasDoubleSpace = /\s\s/.test(element.textContent || '');

    if (!hasDoubleSpace) {
      const style = element.getAttribute('style') || '';
      const newStyle = style
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s && !PRE_WRAP_STYLES.includes(s))
        .join('; ');

      if (newStyle) element.setAttribute('style', newStyle);
      else element.removeAttribute('style');
    }
  });

  // Clean up Redundant/Style-less Tags (span and strong) ---
  const unwrapIfStyleless = (tagName: string) => {
    doc.querySelectorAll(tagName).forEach((element) => {
      const style = element.getAttribute('style') || '';

      if (!style.trim() && element.attributes.length === 0) {
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        }
      }
    });
  };

  unwrapIfStyleless('span');
  unwrapIfStyleless('strong');

  return doc.body.innerHTML;
}

export function htmlToEditorState(editor: LexicalEditor, htmlString: string) {
  editor.update(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlString, 'text/html');
    const nodes = $generateNodesFromDOM(editor, dom);
    const root = $getRoot();
    root.clear();

    // Ensure only element/decorator nodes are appended
    nodes.forEach((node) => {
      if (node.isInline() || node.getType() === "text") {
        // Wrap text or inline nodes in a paragraph
        const divBlock = $createCasDivNode();
        divBlock.append(node);
        root.append(divBlock);
      } else {
        root.append(node);
      }
    });
  });
}


const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [TextNode, (editor, target) => target.exportDOM(editor)],
  [TextNode, (editor, target) => target.exportDOM(editor)],
]);

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};

  // Wrap all TextNode importers with a function that also imports
  // the custom styles implemented by the playground
  Object.entries(TextNode.importDOM() || {}).forEach(([tag, fn]) => {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }
          
          const extraStyles = element.getAttribute('style') || '';
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  });
  
  /*
    The compose editor's importer. This editor takes no pasted pictures of its own (it mounts no
    ImagePastePlugin), but a paste from Word still arrives as HTML naming file: pictures - which
    this leaves out rather than turning into broken images.
  */
  importMap['img'] = (node) => ({ conversion: () => $importImageElement(node) });
  return importMap;
};

export type FeatureConfig = {
  treeView?: boolean;
  // Undo/Redo
  undoRedo?: boolean;

  // Text Formatting
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;

  // Alignment
  alignLeft?: boolean;
  alignCenter?: boolean;
  alignRight?: boolean;
  alignJustify?: boolean;

  // List
  lists?: boolean;

  // Custom Feature
  numbers?: boolean;
};

const SIMPLE_FEATURES_CONFIG: FeatureConfig = {
  undoRedo: true,
  bold: true,
  italic: true,
  underline: true,
  strikethrough: true,
  alignLeft: true,
  alignCenter: true,
  alignRight: true,
  alignJustify: true,
  lists: true,
};

const FULL_FEATURES_CONFIG: FeatureConfig = {
  ...SIMPLE_FEATURES_CONFIG,
  numbers: true,
  treeView: true,
};

export default function CasSimpleLexicalRichTextEditor({
  label,
  placeholder = 'Enter some text...',
  isError = false,
  initialValue,
  onChange,
  mode = 'full',
}: Props) {
  const features = mode === 'full' ? FULL_FEATURES_CONFIG : SIMPLE_FEATURES_CONFIG;

  const initialConfig = {
    namespace: 'Simple Lexical Rich Text editor',
    theme: ExampleTheme,
    nodes: [
      TextNode, 
      LinkNode,
      ImageNode,
      LineBreakNode,
      ...(features.lists ? [ListNode, ListItemNode] : []),
      ...(features.numbers ? [CasLexicalInputNumberNode] : []),
      
      // Cas Nodes
      CasDivNode,
      {
          replace: ParagraphNode,
          with: () => $createCasDivNode(),
      },
  ],
    onError(error: Error) {
      throw error;
    },
    html: {
      export: exportMap,
      import: constructImportMap(),
    },
    editorState: (editor: LexicalEditor) => {
      if (initialValue) {
        htmlToEditorState(editor, initialValue);
      }
    },
  };

  const handleEditorChange = (_: EditorState, editor: LexicalEditor) => {
    editor.update(() => {
      const html = $generateHtmlFromNodes(editor);
      const cleanedHtml = prepareHtmlForEmail(html);

      const plainText = cleanedHtml.replace(/<[^>]+>/g, '').trim();
      onChange?.(plainText ? cleanedHtml : '');
    });
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`editor-container ${isError ? 'editor-error' : ''}`}>
        {label && <FormLabel>{label}</FormLabel>}
        <ToolbarPlugin features={features} />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                aria-placeholder={placeholder}
                placeholder={<div className="editor-placeholder">{placeholder}</div>}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <OnChangePlugin onChange={handleEditorChange} />
          {features.numbers && <CasLexicalInputNumberPlugin />}
          {features.numbers && <CasNumberBoxDialogHandlerPlugin />}
          {features.treeView && <TreeViewPlugin />}
          {features.lists && <ListPlugin />}
          <ImagesPlugin />
        </div>
      </div>
    </LexicalComposer>
  );
}
